import { Assets, type Texture } from 'pixi.js';
import { resolveAssetUrl, resolveSheetSpec } from '../assets/overrides';
import { UI_ASSETS } from '../assets/uiAssets';
import type { ArenaBundle, LoadedArena } from '../arenas/types';
import type {
  CharacterAnimationName,
  CharacterBundle,
  SpriteSheetSpec,
} from '../characters/types';
import type { LoadedSkillAssets, SkillBundle } from '../skills/types';
import type { CharacterAnimations, CharacterSfx } from './CharacterSprite';
import type { DamageParticleSet } from './Particles';
import { loadAudio } from './utils/audio';
import { loadSpriteSheetFrames } from './utils/spriteSheet';

// Every loader below resolves its URL / sheet spec through the override layer
// first, so a sprite uploaded via the studio portal loads in place of the
// bundled default. With no overrides set, resolve returns the original.
//
// Resilience: if an OVERRIDE fails to load (bad upload, CORS, offline), fall
// back to the bundled default so one bad designer upload can't brick the game.
// A failing bundled default (no override) still throws - that's a real bug.
async function loadTexture(url: string): Promise<Texture> {
  const resolved = resolveAssetUrl(url);
  if (resolved === url) return Assets.load<Texture>(url);
  try {
    return await Assets.load<Texture>(resolved);
  } catch (err) {
    console.warn(`[assets] override failed for ${url}; using bundled default`, err);
    return Assets.load<Texture>(url);
  }
}

// Loaded particle textures grouped by trigger event. Mirrors
// CharacterParticleUrls (specs) but post-load - each entry pairs a Texture
// with the per-particle target render size from the bundle.
export type CharacterParticles = {
  damage?: DamageParticleSet;
};

async function loadSheet(spec: SpriteSheetSpec): Promise<Texture[]> {
  const s = resolveSheetSpec(spec);
  if (s.url === spec.url) {
    return loadSpriteSheetFrames(s.url, s.cols, s.rows, { autoTrim: s.autoTrim });
  }
  // Override sheet - fall back to the bundled sheet (with its own grid) on failure.
  try {
    return await loadSpriteSheetFrames(s.url, s.cols, s.rows, { autoTrim: s.autoTrim });
  } catch (err) {
    console.warn(`[assets] override sheet failed for ${spec.url}; using bundled default`, err);
    return loadSpriteSheetFrames(spec.url, spec.cols, spec.rows, { autoTrim: spec.autoTrim });
  }
}

async function loadOptionalSheet(spec: SpriteSheetSpec | undefined): Promise<Texture[] | undefined> {
  if (!spec) return undefined;
  return loadSheet(spec);
}

// Loads all sprite sheets declared in the bundle. Returns undefined if the
// bundle has no `assets` (stat-only character - falls back to placeholder rect).
export async function loadCharacterAnimations(
  bundle: CharacterBundle,
): Promise<CharacterAnimations | undefined> {
  const a = bundle.assets;
  if (!a) return undefined;
  const [idle, block, run, punch, kick, pain, painAir, dodge] = await Promise.all([
    loadSheet(a.idle),
    loadOptionalSheet(a.block),
    loadOptionalSheet(a.run),
    loadOptionalSheet(a.punch),
    loadOptionalSheet(a.kick),
    loadOptionalSheet(a.pain),
    loadOptionalSheet(a.painAir),
    loadOptionalSheet(a.dodge),
  ]);
  return {
    idle,
    ...(block && { block }),
    ...(run && { run }),
    ...(punch && { punch }),
    ...(kick && { kick }),
    ...(pain && { pain }),
    ...(painAir && { painAir }),
    ...(dodge && { dodge }),
  };
}

// Loads particle textures declared in the bundle. Empty object if none.
// Each spec's `size` rides along into the loaded set so the spawner uses
// per-particle target sizes instead of one global constant.
export async function loadCharacterParticles(
  bundle: CharacterBundle,
): Promise<CharacterParticles> {
  const p = bundle.particles;
  if (!p) return {};
  const damage = p.damage
    ? await Promise.all(
        p.damage.map(async (spec) => ({
          texture: await loadTexture(spec.url),
          size: spec.size,
        })),
      )
    : undefined;
  return damage ? { damage } : {};
}

// Loads any registered SFX clips for the character. Empty object if none.
export async function loadCharacterSfx(bundle: CharacterBundle): Promise<CharacterSfx> {
  if (!bundle.sfx) return {};
  const entries = await Promise.all(
    Object.entries(bundle.sfx).map(async ([name, url]) => {
      const audio = await loadAudio(resolveAssetUrl(url));
      return [name as CharacterAnimationName, audio] as const;
    }),
  );
  return Object.fromEntries(entries) as CharacterSfx;
}

// ----- Arena -----

// Loads all assets an arena bundle declares (sprite sheets + static textures).
// Returned LoadedArena is consumed by BattleScene which gives the bundle a
// layer to paint into via bundle.mount().
export async function loadArena(bundle: ArenaBundle): Promise<LoadedArena> {
  const sheetEntries = Object.entries(bundle.assets.sheets ?? {});
  const textureEntries = Object.entries(bundle.assets.textures ?? {});

  const [loadedSheets, loadedTextures] = await Promise.all([
    Promise.all(sheetEntries.map(([, spec]) => loadSheet(spec))),
    Promise.all(textureEntries.map(([, url]) => loadTexture(url))),
  ]);

  const sheets: Record<string, Texture[]> = {};
  sheetEntries.forEach(([key], i) => {
    sheets[key] = loadedSheets[i]!;
  });
  const textures: Record<string, Texture> = {};
  textureEntries.forEach(([key], i) => {
    textures[key] = loadedTextures[i]!;
  });

  return { bundle, sheets, textures };
}

// ----- Skills -----

// Loads all assets a skill bundle declares (sprite sheets + static textures
// + sfx clips). Returns empty maps if bundle has no assets - skill render
// hook handles that (or there's no hook at all for engine-only skills like
// sparky_bunker).
export async function loadSkillAssets(bundle: SkillBundle): Promise<LoadedSkillAssets> {
  const sheetEntries = Object.entries(bundle.assets?.sheets ?? {});
  const textureEntries = Object.entries(bundle.assets?.textures ?? {});
  const sfxEntries = Object.entries(bundle.assets?.sfx ?? {});

  const [loadedSheets, loadedTextures, loadedSfx] = await Promise.all([
    Promise.all(sheetEntries.map(([, spec]) => loadSheet(spec))),
    Promise.all(textureEntries.map(([, url]) => loadTexture(url))),
    Promise.all(sfxEntries.map(([, url]) => loadAudio(resolveAssetUrl(url)))),
  ]);

  const sheets: Record<string, Texture[]> = {};
  sheetEntries.forEach(([key], i) => {
    sheets[key] = loadedSheets[i]!;
  });
  const textures: Record<string, Texture> = {};
  textureEntries.forEach(([key], i) => {
    textures[key] = loadedTextures[i]!;
  });
  const sfx: Record<string, HTMLAudioElement> = {};
  sfxEntries.forEach(([key], i) => {
    sfx[key] = loadedSfx[i]!;
  });

  return { sheets, textures, sfx };
}

// ----- Desktop / meta UI -----

// Static textures for the desktop meta screen (main-bg, game window, enemy
// card parts). Loaded once at bootstrap, consumed by src/screens.
export type UiTextures = {
  mainBg: Texture;
  // Window frame with a TRANSPARENT hole where the paper area is - drawn
  // OVER the tab content so the frame edges overlap e.g. the city map.
  gameFrame: Texture;
  // The paper sheet alone (same canvas size as the frame). Shown under the
  // content on tabs that read as "drawn on paper"; hidden on the city tab.
  paper: Texture;
  enemyCardBody: Texture;
  enemyAvatar: Texture;
  labelBg: Texture;
  selectBtn: Texture;
  // Default-attack icons for the battle-script builder (punch/kick have no
  // skill bundle, so they are loaded here). 188x172, matching skill logIcons.
  punchIcon: Texture;
  kickIcon: Texture;
  // Ornate frame parts for modals: a vertical border strip (tiled along edges)
  // and a top-right corner (mirrored for the other three). See OrnateBorder.
  border: Texture;
  corner: Texture;
};

// Loads every UI texture declared in UI_ASSETS (shared with the studio catalog)
// through the override layer, keyed by the UiTextures field name.
export async function loadUiTextures(): Promise<UiTextures> {
  const entries = await Promise.all(
    UI_ASSETS.map(async (a) => [a.key, await loadTexture(a.url)] as const),
  );
  return Object.fromEntries(entries) as UiTextures;
}

// ----- Fonts -----

// Meta-UI (desktop screen) font. The battle title font is caps-only and too
// decorative for menu text - Roboto for now, falling back to any sans-serif
// where it is not installed.
export const UI_FONT_FAMILY = 'Roboto, sans-serif';

// Family name is exported because it's also passed into Pixi Text styles
// (BattleScene -> HPBar / FloatingText) - it's the semantic id, not just a path.
export const TITLE_FONT_FAMILY = 'Kids Magazine';
const TITLE_FONT_URL = '/assets/fonts/main/Kids_Magazine.ttf';

export async function loadTitleFont(): Promise<void> {
  await Assets.load({ src: TITLE_FONT_URL, data: { family: TITLE_FONT_FAMILY } });
}
